import useGetShopExchangeOptionNames from "@babackapp/private-api-client/client/use-get-shop-exchange-option-names";
import FormTagsInput from "@babackapp/react-hook-forms-components/form-tags-input";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Plus } from "lucide-react";
import { forwardRef, type ComponentProps } from "react";
import { useParams } from "react-router-dom";

// Same comparison as the back (`normalizeOption`): « TAILLE » = « Taille ».
const normalize = (name: string) => name.trim().toLowerCase();

const MAX_SUGGESTIONS = 8;

/**
 * Locked exchange options (BAB-3371), with the option names of the synced
 * catalog: the common ones are offered in one click, and a name no product
 * carries is flagged, since it locks nothing.
 */
const LockedOptionsInput = forwardRef(
  (
    {
      value = [],
      onChange,
      onBlur,
      disabled,
      ...props
    }: ComponentProps<typeof FormTagsInput>,
    ref,
  ) => {
    const t = useTranslation();
    const { shopId = "" } = useParams<{ shopId: string }>();
    const { data } = useGetShopExchangeOptionNames({ shopId });
    const options = data?.options ?? [];

    const known = new Set(options.map((option) => normalize(option.name)));
    const picked = new Set(value.map(normalize));
    // Without a synced catalog there is nothing to compare with.
    const tagWarnings = new Map(
      options.length
        ? value
            .filter((name) => !known.has(normalize(name)))
            .map((name) => [
              name,
              t("settings.exchange.fields.lockedOptions.unknownOption", {
                option: name,
              }),
            ])
        : [],
    );
    // Most common first (sorted by the back); an option on a single product
    // is mostly a one-off spelling.
    const suggestions = options
      .filter(
        (option) =>
          option.productCount > 1 && !picked.has(normalize(option.name)),
      )
      .slice(0, MAX_SUGGESTIONS);

    return (
      <div className="flex flex-col gap-2">
        <FormTagsInput
          {...props}
          ref={ref}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          tagWarnings={tagWarnings}
        />
        {suggestions.length > 0 && !disabled && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t("settings.exchange.fields.lockedOptions.suggestions")}
            </span>
            {suggestions.map((option) => (
              <Button
                key={option.name}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  // Like a typed tag: marks the field touched.
                  onBlur?.();
                  onChange([...value, option.name]);
                }}
              >
                <Plus className="size-3" />
                {option.name}
                <span className="max-w-48 truncate font-normal text-muted-foreground">
                  {option.sampleValues.join(", ")}
                </span>
              </Button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

LockedOptionsInput.displayName = "LockedOptionsInput";

export default LockedOptionsInput;
